import { createContext, useContext, useEffect, useState, useSyncExternalStore } from "react";
import { flags as defaultFlags } from "./openfeature-adapter";
import type { FeatureFlags, FlagsConfig, FlagValue } from "./types";

const FlagsContext = createContext<FeatureFlags>(defaultFlags);

export interface FlagsProviderProps {
  /** Bootstrap config (initial evaluation context). */
  config?: FlagsConfig;
  /** Inject a custom client (e.g. a mock in tests). Defaults to the shared one. */
  client?: FeatureFlags;
  children: React.ReactNode;
}

/**
 * FlagsProvider — initializes the flags client once and exposes it via context.
 *
 * Compose this at the app layer (alongside AnalyticsProvider/AuthProvider),
 * never inside another integration package.
 */
export function FlagsProvider({ config, client, children }: FlagsProviderProps) {
  const value = client ?? defaultFlags;
  // Init exactly once per client instance — a lazy `useState` initializer (not a ref read/write during
  // render, and not an effect: `useFlag` reads synchronously off `value` from the first render, so the
  // init call must be issued no later than that first render too).
  useState(() => {
    void value.init(config);
    return true;
  });
  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>;
}

/** Access the raw flags client from context. */
export function useFlags(): FeatureFlags {
  return useContext(FlagsContext);
}

/**
 * useFlag — subscribe to a single flag's value and re-render on change.
 *
 * The default value's type selects the evaluation method (boolean/string/number),
 * keeping the hook fully type-safe at the call site.
 */
export function useFlag(key: string, defaultValue: boolean): boolean;
export function useFlag(key: string, defaultValue: string): string;
export function useFlag(key: string, defaultValue: number): number;
export function useFlag(key: string, defaultValue: FlagValue): FlagValue {
  const client = useFlags();

  const subscribe = (onStoreChange: () => void) => client.onChange(onStoreChange);

  const getSnapshot = (): FlagValue => {
    if (typeof defaultValue === "boolean") return client.getBoolean(key, defaultValue);
    if (typeof defaultValue === "number") return client.getNumber(key, defaultValue);
    return client.getString(key, defaultValue);
  };

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * useFlagsContext — imperatively push a new evaluation context (e.g. on login).
 * Returns nothing; effects re-run when `targetingKey` changes.
 */
export function useFlagsContext(targetingKey: string | undefined): void {
  const client = useFlags();
  useEffect(() => {
    if (targetingKey) void client.setContext({ targetingKey });
  }, [client, targetingKey]);
}
